import { sandboxPay, verifyShopPayment } from '@/api/shop';

/**
 * Pay a placed shop order: `{ order, checkout }` from POST /commerce/orders or
 * GET /commerce/orders/:id/checkout. Locally the backend runs a fake gateway,
 * which we "pay" through the sandbox endpoint (dev builds only); anywhere else
 * it opens Razorpay via the site's existing `useRazorpay().initiatePayment`.
 * Both paths end in verify-payment.
 *
 * Callbacks: onPaid(orderId), onVerifyError(err, orderId), onError(message), onDismiss().
 */
export const payShopOrder = async ({ order, checkout, initiatePayment, user, onPaid, onVerifyError, onError, onDismiss }) => {
  const verify = async (payload) => {
    try {
      await verifyShopPayment(order.id, payload);
      onPaid(order.id);
    } catch (err) {
      onVerifyError(err, order.id);
    }
  };

  if (checkout.gateway === 'fake') {
    if (!import.meta.env.DEV) {
      onError('Online payment is not available right now');
      return;
    }
    try {
      const sim = await sandboxPay(checkout.gatewayOrderId);
      await verify({ gatewayOrderId: checkout.gatewayOrderId, gatewayPaymentId: sim.payment.id, signature: sim.signature });
    } catch {
      onError('Test payment failed');
    }
    return;
  }

  initiatePayment(
    { id: checkout.gatewayOrderId, amount: checkout.amountPaise, currency: checkout.currency || 'INR' },
    {
      key: checkout.key,
      description: `LK Shop order ${order.orderNo}`,
      prefill: { name: user?.name, contact: user?.phone, email: user?.email },
      theme: { color: '#1a1a1a' },
      handler: (res) =>
        verify({
          gatewayOrderId: res.razorpay_order_id,
          gatewayPaymentId: res.razorpay_payment_id,
          signature: res.razorpay_signature,
        }),
      onFailure: (e) => onError(e?.description || 'Payment failed. You can try again.'),
      modal: { ondismiss: onDismiss },
    }
  );
};
